'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import SiteInquiryForm from '@/components/contact/SiteInquiryForm';
import HomeFaqPreviewSection from '@/components/section/home/HomeFaqPreviewSection';
import { SITE_CONTACT_EMAIL, SITE_CONTACT_MAILTO } from '@/constants/siteContact';
import { HELP_FAQ_SECTION_ID, HELP_SUPPORT_FORM_ID } from '@/constants/helpCenter';
import { FAQ_OPEN_EVENT, type FAQItem } from '@/components/section';
import { PAGE_SECTION_BAND_ELEVATED, PAGE_SECTION_BAND_MUTED, pageSectionShell } from '@/constants/pageSections';
import AppLink from '@/components/i18n/AppLink';
import { Button, H3, Input, Li, P, Span, Ul, SURFACE_REVEAL, SURFACE_STATIC, surfaceCard } from '@/components/ui';
import type { SpecialLinkCard } from '@/components/pages/marketing/special/types';
import { filterHelpTopics, resolveHelpLinkCards } from '@/lib/help/helpTopicSearch';
import { openHelpSupportFromHash, scrollToHelpFaqItem } from '@/lib/help/scrollToHelpTarget';
import HelpSupportFormDialog from './HelpSupportFormDialog';

export type HelpCenterSectionProps = {
  overviewTitle: string;
  overviewBody: string;
  searchPlaceholder: string;
  searchResultsTitle: string;
  searchNoResults: string;
  searchSupportCta: string;
  categoriesTitle: string;
  categories: readonly SpecialLinkCard[];
  quickLinksTitle: string;
  quickLinks: readonly SpecialLinkCard[];
  supportTitle: string;
  supportSubtitle: string;
  formLabels: {
    submit: string;
    name: string;
    email: string;
    company: string;
    message: string;
  };
  faqTitle: string;
  faqSubtitle?: string;
  faqItems: readonly FAQItem[];
  contactEmailLabel?: string;
  learnMoreLabel: string;
  dialogCloseLabel?: string;
  className?: string;
};

export default function HelpCenterSection({
  overviewTitle,
  overviewBody,
  searchPlaceholder,
  searchResultsTitle,
  searchNoResults,
  searchSupportCta,
  categoriesTitle,
  categories,
  quickLinksTitle,
  quickLinks,
  supportTitle,
  supportSubtitle,
  formLabels,
  faqTitle,
  faqSubtitle,
  faqItems,
  contactEmailLabel = 'Email our team',
  learnMoreLabel,
  dialogCloseLabel = 'Close',
  className,
}: HelpCenterSectionProps) {
  const [query, setQuery] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);

  const safeCategories = useMemo(() => resolveHelpLinkCards(categories), [categories]);
  const safeQuickLinks = useMemo(() => resolveHelpLinkCards(quickLinks), [quickLinks]);

  const searchResults = useMemo(
    () => filterHelpTopics(query, safeCategories, safeQuickLinks, faqItems),
    [query, safeCategories, safeQuickLinks, faqItems]
  );

  const isSearching = query.trim().length > 0;

  const openSupportForm = useCallback(() => {
    setFormDialogOpen(true);
  }, []);

  useEffect(() => {
    openHelpSupportFromHash({ onOpenDialog: openSupportForm });
    const onHashChange = () => openHelpSupportFromHash({ onOpenDialog: openSupportForm });
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [openSupportForm]);

  const handleFaqResultClick = (faqId: string) => {
    setQuery('');
    window.dispatchEvent(new CustomEvent(FAQ_OPEN_EVENT, { detail: { id: faqId } }));
    window.setTimeout(() => scrollToHelpFaqItem(faqId), 80);
  };

  return (
    <div className={clsx('page-help-root', className)}>
      <HelpSupportFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        title={supportTitle}
        subtitle={supportSubtitle}
        formLabels={formLabels}
        closeLabel={dialogCloseLabel}
      />

      <section
        className={clsx(pageSectionShell(PAGE_SECTION_BAND_ELEVATED), '!min-h-0 pb-0')}
        aria-label={searchPlaceholder}
      >
        <div className={clsx(surfaceCard(), 'mx-auto max-w-3xl', SURFACE_STATIC)}>
          <label className="block w-full" htmlFor="help-search-input">
            <Span className="label mb-2">{searchPlaceholder}</Span>
            <Input
              id="help-search-input"
              type="search"
              name="help-search"
              className="input w-full"
              placeholder={searchPlaceholder}
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>

          {isSearching ? (
            <div className="page-help-search-results mt-4" role="region" aria-live="polite">
              <H3 className="m-0 text-sm font-semibold text-prose">{searchResultsTitle}</H3>
              {searchResults.length > 0 ? (
                <Ul className="page-help-search-list mt-3 list-none p-0">
                  {searchResults.map((result) => (
                    <Li key={`${result.kind}-${result.title}-${result.href}`}>
                      {result.kind === 'faq' && result.faqId ? (
                        <button
                          type="button"
                          className="page-help-search-hit"
                          onClick={() => handleFaqResultClick(result.faqId!)}
                        >
                          <Span className="page-help-search-hit__kind">FAQ</Span>
                          <Span className="page-help-search-hit__title">{result.title}</Span>
                          {result.text ? (
                            <Span className="page-help-search-hit__text">{result.text}</Span>
                          ) : null}
                        </button>
                      ) : (
                        <AppLink href={result.href} className="page-help-search-hit">
                          <Span className="page-help-search-hit__kind">
                            {result.kind === 'category' ? categoriesTitle : quickLinksTitle}
                          </Span>
                          <Span className="page-help-search-hit__title">{result.title}</Span>
                          {result.text ? (
                            <Span className="page-help-search-hit__text">{result.text}</Span>
                          ) : null}
                        </AppLink>
                      )}
                    </Li>
                  ))}
                </Ul>
              ) : (
                <div className="mt-3 space-y-3">
                  <P className="m-0 text-sm text-prose-muted">{searchNoResults}</P>
                  <Button type="button" variant="primary" onClick={openSupportForm}>
                    {searchSupportCta}
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </section>

      <div className="fixed bottom-4 left-4 right-4 z-40 flex justify-center lg:hidden">
        <Button
          type="button"
          variant="primary"
          className="shadow-lg"
          onClick={openSupportForm}
          aria-controls={HELP_SUPPORT_FORM_ID}
        >
          {supportTitle}
        </Button>
      </div>

      <section className="page-consult-band container-section" aria-labelledby="help-overview-heading">
        <div className={clsx('page-content-grid page-consult-layout', isSearching && 'hidden')}>
          <div className="min-w-0 space-y-8">
            <article className={surfaceCard()}>
              <H3 id="help-overview-heading" className="m-0 text-xl font-semibold text-prose">
                {overviewTitle}
              </H3>
              <P className="m-0 mt-3 text-sm leading-relaxed text-prose-muted md:text-base">{overviewBody}</P>
            </article>

            <div>
              <H3 className="m-0 text-sm font-semibold uppercase tracking-[0.14em] text-prose-muted">
                {categoriesTitle}
              </H3>
              <Ul className="page-help-topic-grid mt-4 list-none p-0">
                {safeCategories.map((card) => (
                  <Li key={card.href}>
                    <AppLink href={card.href} className="page-help-topic-card block h-full">
                      <Span className="block text-base font-semibold text-prose">{card.title}</Span>
                      <Span className="mt-2 block text-sm leading-relaxed text-prose-muted">{card.text}</Span>
                      <Span className="mt-3 inline-block text-sm font-medium text-[var(--color-primary)]">
                        {learnMoreLabel}
                      </Span>
                    </AppLink>
                  </Li>
                ))}
              </Ul>
            </div>

            {safeQuickLinks.length > 0 ? (
              <div className={surfaceCard()}>
                <H3 className="m-0 text-base font-semibold text-prose">{quickLinksTitle}</H3>
                <Ul className="page-help-quick-links mt-3">
                  {safeQuickLinks.map((link) => (
                    <Li key={link.href}>
                      <AppLink href={link.href}>{link.title}</AppLink>
                    </Li>
                  ))}
                </Ul>
              </div>
            ) : null}

            <div className="page-glass-panel page-glass-panel--elevated page-glass-panel--compact page-glass-panel--strip page-glass-contact-strip">
              <P className="m-0 text-sm text-prose-muted">{contactEmailLabel}</P>
              <a
                href={SITE_CONTACT_MAILTO}
                className="page-contact-link"
              >
                {SITE_CONTACT_EMAIL}
              </a>
            </div>
          </div>

          <aside
            id={HELP_SUPPORT_FORM_ID}
            className={clsx(
              'page-consult-form-panel page-glass-panel page-glass-panel--elevated help-support-form-anchor min-w-0 lg:sticky lg:top-24 lg:self-start',
              SURFACE_STATIC
            )}
          >
            <H3 className="m-0 text-xl font-semibold tracking-tight text-prose sm:text-2xl">{supportTitle}</H3>
            <P className="m-0 mt-2 text-sm leading-relaxed text-prose-muted sm:text-base">{supportSubtitle}</P>
            <SiteInquiryForm
              className="page-inquiry-form mt-6"
              defaultType="support"
              showTypeSelect
              labels={formLabels}
            />
          </aside>
        </div>
      </section>

      <HomeFaqPreviewSection
        title={faqTitle}
        subtitle={faqSubtitle}
        items={faqItems}
        sectionId={HELP_FAQ_SECTION_ID}
        faqItemIdPrefix="faq"
        className={PAGE_SECTION_BAND_MUTED}
      />
    </div>
  );
}
